import config from 'config';
import { AxiosResponse } from 'axios';
import { ListPayload, ListResponse, SuccessResponse } from 'core/http/response';
import { CreateDocumentPayload } from 'store/documents/document';
import {
    Customer,
    CustomerFilePresignedUrlResponse,
    CustomerParams,
    DocumentResponse,
    ListCustomerFilesParams,
} from './customer';
import axiosClient from '../../config/axios';

export const listCustomers = (params?: Partial<CustomerParams>): Promise<ListPayload<Customer>> =>
    axiosClient
        .get<ListResponse<Customer>>(`${config.cmsUrl}/customers`, { params })
        .then((response) => response.data.payload);

export const getAllCustomers = async (itemsPerPage = 50): Promise<Customer[]> => {
    try {
        const allCustomers: Customer[] = [];

        let currentPage = 0;
        let totalPages = +Infinity;

        while (currentPage < totalPages) {
            const response = await listCustomers({
                page: currentPage,
                size: itemsPerPage,
            });

            const { list, total } = response;

            if (!total || !list.length) {
                break;
            }

            allCustomers.push(...list);

            totalPages = total;
            currentPage += 1;
        }

        return allCustomers;
    } catch (error) {
        return [];
    }
};

export const listArchivedCustomers = (params?: Partial<CustomerParams>): Promise<ListPayload<Customer>> =>
    axiosClient
        .get<ListResponse<Customer>>(`${config.cmsUrl}/customers/archived`, { params })
        .then((response) => response.data.payload);

export const createCustomer = (customer: Customer): Promise<Customer> => {
    const { customerDetails, ...customerRequest } = customer;

    return axiosClient
        .post<SuccessResponse<Customer>>(`${config.cmsUrl}/customers`, { customerRequest, customerDetails })
        .then((response) => response.data.payload);
};

export const editCustomer = (customerId: string, customer: Partial<Customer>): Promise<Customer> =>
    axiosClient
        .put<SuccessResponse<Customer>>(`${config.cmsUrl}/customers/${customerId}`, customer)
        .then((response) => response.data.payload);

export const getCustomer = (customerId: string): Promise<Customer> =>
    axiosClient
        .get<SuccessResponse<Customer>>(`${config.cmsUrl}/customers/${customerId}`)
        .then((response) => response.data.payload);

export const deleteCustomer = (customerId: string): Promise<SuccessResponse<void>> =>
    axiosClient.delete<SuccessResponse<void>>(`${config.cmsUrl}/customers/${customerId}`).then((response) => {
        return response.data;
    });

export const connectToHmrc = (customerID: string): Promise<any> =>
    axiosClient.get<any>(`${config.cmsUrl}/customers/cds/redirect?id=${customerID}`).then((response) => {
        return response;
    });

export const saveHmrcToken = (customerID: string, code: string): Promise<any> =>
    axiosClient.get<any>(`${config.cmsUrl}/customers/cds/save?id=${customerID}&code=${code}`).then((response) => {
        return response;
    });

export const disconnectToHmrc = (customerID: string): Promise<any> =>
    axiosClient.get<any>(`${config.cmsUrl}/customers/cds/disconnect?id=${customerID}`).then((response) => {
        return response;
    });

export const archiveCustomer = (customerId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .put<SuccessResponse<void>>(`${config.cmsUrl}/customers/archive/${customerId}`)
        .then((response) => response.data);
export const unarchiveCustomer = (customerId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .put<SuccessResponse<void>>(`${config.cmsUrl}/customers/unarchive/${customerId}`)
        .then((response) => response.data);

export const getCustomerProfilePictureUploadPreSign = (
    customerId: string,
    params: { customerId: string; pictureName: string; pictureSize: number }
): Promise<string> =>
    axiosClient
        .get<SuccessResponse<string>>(`${config.cmsUrl}/customers/profile-picture/${customerId}/upload`, {
            params,
        })
        .then((response) => {
            return response.data.payload;
        });

export const getCustomerProfilePictureDownloadPreSign = (customerId: string): Promise<string | null> =>
    axiosClient
        .get<SuccessResponse<string>>(`${config.cmsUrl}/customers/profile-picture/${customerId}/download`)
        .then((response) => {
            return response.data.payload;
        })
        .catch((error) => {
            if (error.response?.status === 404) return null;
            throw error;
        });

export const uploadCustomerProfilePicture = (customerId: string, s3key: string): Promise<string> =>
    axiosClient
        .post<SuccessResponse<Customer & { profilePictureLink: string }>>(
            `${config.cmsUrl}/customers/profile-picture/${customerId}`,
            undefined,
            { params: { customerId, s3key } }
        )
        .then((response) => {
            return response.data.payload.profilePictureLink;
        });

export const deleteCustomerProfilePicture = (customerId: string): Promise<void> =>
    axiosClient
        .delete<void>(`${config.cmsUrl}/customers/profile-picture/${customerId}`)
        .then((response) => response.data);

export const exportCustomers = async (): Promise<Blob> => {
    const response: AxiosResponse<Blob> = await axiosClient.get(`${config.cmsUrl}/customers/export`, {
        headers: { Accept: 'text/csv', 'Content-Type': 'text/csv' },
        responseType: 'blob',
    });

    return response.data;
};

export const getDocumentDownloadUrl = async (
    customerId: string,
    fileId: string
): Promise<CustomerFilePresignedUrlResponse> => {
    const response: AxiosResponse<SuccessResponse<CustomerFilePresignedUrlResponse>> = await axiosClient.get(
        `${config.cmsUrl}/customers/files/${customerId}/download/${fileId}`
    );
    return response.data.payload;
};

export const getDocumentUploadUrl = async (
    customerId: string,
    fileName: string,
    fileSize: number
): Promise<CustomerFilePresignedUrlResponse> => {
    const response: AxiosResponse<SuccessResponse<CustomerFilePresignedUrlResponse>> = await axiosClient.get(
        `${config.cmsUrl}/customers/files/upload/${customerId}`,
        {
            params: {
                fileName,
                fileSize,
            },
        }
    );
    return response.data.payload;
};

export const createCustomerFile = async (
    customerId: string,
    payload: CreateDocumentPayload
): Promise<DocumentResponse> => {
    const response: AxiosResponse<SuccessResponse<any>> = await axiosClient.post(
        `${config.cmsUrl}/customers/files/${customerId}`,
        payload
    );
    return response.data.payload;
};

export const listCustomerFiles = async (
    customerId: string,
    params?: ListCustomerFilesParams
): Promise<ListPayload<DocumentResponse>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<DocumentResponse>>> = await axiosClient.post(
        `${config.cmsUrl}/customers/files/list/${customerId}`,
        {},
        { params }
    );
    return response.data.payload;
};

export const archiveCustomerFile = async (customerId: string, fileId: string): Promise<DocumentResponse> => {
    const response: AxiosResponse<SuccessResponse<DocumentResponse>> = await axiosClient.post(
        `${config.cmsUrl}/customers/files/${customerId}/archive/${fileId}`
    );
    return response.data.payload;
};

export const unarchiveCustomerFile = async (customerId: string, fileId: string): Promise<DocumentResponse> => {
    const response: AxiosResponse<SuccessResponse<DocumentResponse>> = await axiosClient.post(
        `${config.cmsUrl}/customers/files/${customerId}/unarchive/${fileId}`
    );
    return response.data.payload;
};

export const deleteCustomerFile = async (customerId: string, fileId: string): Promise<{}> => {
    const response: AxiosResponse<SuccessResponse<{}>> = await axiosClient.delete(
        `${config.cmsUrl}/customers/files/${customerId}/delete/${fileId}`
    );
    return response.data.payload;
};
